import {
  Block,
  BlockFooter,
  Button,
  List,
  ListItem,
  Page,
  Popup,
  Segmented,
} from "framework7-react";
import { useState } from "react";
import PopupNavbar from "../components/popup-navbar";
import { usePopupBackClose } from "../hooks/use-back-close";
import { formatAmountInput, formatCents } from "../utils/format";
import {
  SHARE_MODES,
  SPLIT_MESSAGES,
  describePercentRemainder,
  equalSplit,
  formatSplitValue,
  getRemainingCents,
  isValueMode,
  parseCents,
  parseSplitValue,
  removeLastDigit,
  selectSplitReceivers,
  setSplitShare,
  setSplitValue,
  sumValues,
  switchShareMode,
  toggleSplitReceiver,
} from "../utils/money";
import { describeShareSummary } from "../utils/share-summary";

export const allReceivers = (amount, participants) =>
  equalSplit(
    amount,
    participants.map((p) => ({ id: p.id })),
  );

export const isEveryone = (receivers, participants) =>
  participants.length > 0 &&
  participants.every((p) => receivers.some((r) => r.id === p.id));

export const MODE_LABELS = {
  [SHARE_MODES.equal]: "Gleich",
  [SHARE_MODES.amounts]: "Beträge",
  [SHARE_MODES.weights]: "Anteile",
  [SHARE_MODES.percent]: "Prozent",
};

const unitOf = (mode, value) => {
  if (mode === SHARE_MODES.percent) return "%";
  return value === 100 ? "Anteil" : "Anteile";
};

const describeSummary = (amount, { mode, receivers }) => {
  if (mode === SHARE_MODES.percent) return describePercentRemainder(receivers);
  if (mode === SHARE_MODES.weights && sumValues(receivers) === 0) {
    return { balanced: false, text: SPLIT_MESSAGES.noWeight };
  }
  return describeShareSummary(amount, getRemainingCents(amount, receivers));
};

const blurOnEnter = (e) => {
  if (e.key === "Enter") e.target.blur();
};

// updateSplit takes an updater function, so a value committed on blur and
// a click in the same tick both apply.
const ReceiversPopup = ({
  title = "Für wen wurde bezahlt?",
  opened,
  onClose,
  amount,
  split,
  participants,
  updateSplit,
}) => {
  const [draftShares, setDraftShares] = useState({});
  const [draftValues, setDraftValues] = useState({});
  const backClose = usePopupBackClose(opened);
  const { mode, receivers } = split;
  const valueMode = isValueMode(mode);

  const dropDrafts = () => {
    setDraftShares({});
    setDraftValues({});
  };
  const dropDraft = (id) => {
    setDraftShares(({ [id]: _dropped, ...rest }) => rest);
    setDraftValues(({ [id]: _dropped, ...rest }) => rest);
  };
  const setDraftShare = (id, cents) =>
    setDraftShares((prev) => ({ ...prev, [id]: cents }));

  const toggleReceiver = (id) => {
    dropDraft(id);
    updateSplit((prev) => toggleSplitReceiver(amount, prev, id));
  };

  const everyoneSelected = isEveryone(receivers, participants);
  const toggleAll = () => {
    dropDrafts();
    updateSplit((prev) =>
      selectSplitReceivers(
        amount,
        prev,
        everyoneSelected ? [] : participants.map((p) => p.id),
      ),
    );
  };

  const chooseMode = (next) => {
    dropDrafts();
    updateSplit((prev) => switchShareMode(amount, prev, next));
  };

  const commitShare = (id) => {
    const cents = draftShares[id];
    dropDraft(id);
    if (cents === undefined) return;
    updateSplit((prev) => setSplitShare(amount, prev, id, cents));
  };

  const changeValue = (id, text) => {
    setDraftValues((prev) => ({ ...prev, [id]: text }));
    const value = parseSplitValue(text);
    if (value !== null) {
      updateSplit((prev) => setSplitValue(amount, prev, id, value));
    }
  };

  const applyEqualSplit = () => {
    dropDrafts();
    updateSplit((prev) =>
      switchShareMode(amount, { ...prev, mode: null }, SHARE_MODES.equal),
    );
  };

  const summary = describeSummary(amount, split);

  const shareInput = (p, receiver) => {
    const share = draftShares[p.id] ?? receiver.share;
    return (
      <div slot="content-end" className="receiver-share">
        <input
          name={`share-${p.id}`}
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          autoComplete="off"
          value={formatAmountInput(share)}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setDraftShare(p.id, parseCents(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              setDraftShare(p.id, removeLastDigit(share));
            } else {
              blurOnEnter(e);
            }
          }}
          onBlur={() => commitShare(p.id)}
        />
        <span className="receiver-share-currency">€</span>
      </div>
    );
  };

  const valueInput = (p, receiver) => (
    <div
      slot="content-end"
      className={`receiver-share receiver-split receiver-split-${mode}`}
    >
      <input
        name={`split-${p.id}`}
        type="text"
        inputMode="decimal"
        enterKeyHint="done"
        autoComplete="off"
        value={draftValues[p.id] ?? formatSplitValue(receiver.value)}
        onFocus={(e) => e.target.select()}
        onChange={(e) => changeValue(p.id, e.target.value)}
        onKeyDown={blurOnEnter}
        onBlur={() => dropDraft(p.id)}
      />
      <span className="receiver-split-unit">
        {unitOf(mode, receiver.value)}
      </span>
    </div>
  );

  return (
    <Popup
      {...backClose}
      className="receivers-popup"
      opened={opened}
      onPopupClosed={() => {
        dropDrafts();
        onClose();
      }}
    >
      <Page>
        <PopupNavbar title={title} />
        <Block>
          <Segmented
            strong
            className="receivers-mode"
            role="group"
            aria-label="Aufteilung"
          >
            {Object.values(SHARE_MODES).map((option) => (
              <Button
                key={option}
                small
                active={mode === option}
                className={`receivers-mode-${option}`}
                onClick={() => chooseMode(option)}
              >
                {MODE_LABELS[option]}
              </Button>
            ))}
          </Segmented>
          <div className="receivers-toggle-row">
            <Button small className="receivers-toggle-all" onClick={toggleAll}>
              {everyoneSelected ? "Keine" : "Alle auswählen"}
            </Button>
          </div>
          <List strong className="receivers-list">
            {participants.map((p) => {
              const receiver = receivers.find((r) => r.id === p.id);
              return (
                <ListItem
                  key={p.id}
                  checkbox
                  checked={!!receiver}
                  title={p.name}
                  footer={
                    receiver && valueMode
                      ? formatCents(receiver.share)
                      : undefined
                  }
                  // The row is a <label>: onChange (not onClick), so clicks
                  // into the share input do not toggle the receiver.
                  onChange={() => toggleReceiver(p.id)}
                >
                  {receiver &&
                    (valueMode
                      ? valueInput(p, receiver)
                      : shareInput(p, receiver))}
                </ListItem>
              );
            })}
          </List>

          <BlockFooter
            className={`share-summary${summary.balanced ? "" : " text-color-red"}`}
          >
            {summary.text}
          </BlockFooter>

          {mode === SHARE_MODES.amounts && (
            <Block>
              <Button fill onClick={applyEqualSplit}>
                Betrag gleich verteilen
              </Button>
            </Block>
          )}
        </Block>
      </Page>
    </Popup>
  );
};

export default ReceiversPopup;
